import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });

  const load = (search = "") => api.get(`/customers${search ? `?q=${search}` : ""}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const startNew = () => { setEditing(null); setForm({ name: "", phone: "", email: "", address: "" }); setOpen(true); };
  const startEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone, email: c.email || "", address: c.address || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success("Saved"); setOpen(false); load(q);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(q); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="customers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Customers</h1>
          <p className="text-sm text-[#7A7873] mt-1">Build loyalty with returning guests.</p>
        </div>
        <Button onClick={startNew} className="bg-[#D95D39] hover:bg-[#C24F2C]" data-testid="add-customer-btn">
          <Plus size={16} className="mr-1.5" /> Add Customer
        </Button>
      </div>

      <div className="relative w-80 mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7873]" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone…" className="pl-9 h-10 border-[#EBE8E2]" data-testid="customer-search" />
      </div>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Phone</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-right px-4 py-2.5">Visits</th>
              <th className="text-right px-4 py-2.5">Total Spent</th>
              <th className="text-right px-4 py-2.5">Points</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-[#EBE8E2]">
                <td className="px-4 py-2.5 font-medium">{c.name}</td>
                <td className="px-4 py-2.5 font-mono-num text-[#7A7873]">{c.phone}</td>
                <td className="px-4 py-2.5 text-[#7A7873]">{c.email || "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{c.visits || 0}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(c.total_spent || 0)}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBE9E2] text-[#D95D39] font-semibold font-mono-num">
                    {c.loyalty_points || 0}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(c)}>
                    <Pencil size={13} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => del(c)}>
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#7A7873]">No customers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={save} data-testid="save-customer">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
